import { Field, Input, InputArea, Select } from '@cloudflare/kumo';
import { FormPage, FormSection } from './ui/FormPage';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { ContactInput, Contact } from '../types';
import { apiService } from '../services/api';
import { validatePhone } from '../utils/phoneUtils';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { ContactCustomFields, CustomFieldDef } from './ContactCustomFields';

interface Category {
  id: string;
  nome: string;
}

/** O `Select` do Cloudscape não aceita valor vazio: "sem categoria" vira esta sentinela. */
const NO_CATEGORY = '__none__';

const contactSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(validatePhone, {
    message: 'Formato de telefone inválido',
  }),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  observacoes: z.string().optional(),
  dataNascimento: z.string().optional(),
  categoriaId: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ContactForm({ contact, onSuccess, onCancel }: ContactFormProps) {
  const toast = useAppToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const fetchApi = useAuthenticatedFetch();
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [initialFieldValues, setInitialFieldValues] = useState<Record<string, string>>({});

  // Campos personalizados da organização e os valores deste contato
  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await fetchApi('/api/custom-fields').catch(() => null);
      if (!res?.ok || !alive) return;
      setCustomFields((await res.json()).fields || []);
      if (contact?.id) {
        const v = await fetchApi(`/api/custom-fields/contact/${contact.id}`).catch(() => null);
        if (v?.ok && alive) {
          const vals = Object.fromEntries(Object.entries((await v.json()).values || {}).map(([k, x]) => [k, (x as { value: string }).value]));
          setFieldValues(vals);
          setInitialFieldValues(vals);
        }
      }
    })();
    return () => { alive = false; };
  }, [fetchApi, contact?.id]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nome: contact?.nome || '',
      telefone: contact?.telefone || '',
      email: contact?.email || '',
      observacoes: contact?.observacoes || '',
      dataNascimento: contact?.dataNascimento ? contact.dataNascimento.slice(0, 10) : '',
      categoriaId: contact?.categoriaId || '',
    },
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (contact && !loadingCategories) {
      reset({
        nome: contact.nome || '',
        telefone: contact.telefone || '',
        email: contact.email || '',
        observacoes: contact.observacoes || '',
        dataNascimento: contact.dataNascimento ? contact.dataNascimento.slice(0, 10) : '',
        categoriaId: contact.categoriaId || '',
      });

      setTimeout(() => {
        if (contact.categoriaId) {
          setValue('categoriaId', contact.categoriaId);
        }
      }, 100);
    }
  }, [contact, categories, loadingCategories, reset, setValue]);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/categorias', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch {
      // silently ignore
    } finally {
      setLoadingCategories(false);
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      const contactInput: ContactInput = {
        nome: data.nome,
        telefone: data.telefone,
        email: data.email || undefined,
        observacoes: data.observacoes || undefined,
        categoriaId: data.categoriaId || undefined,
        dataNascimento: data.dataNascimento || (contact?.dataNascimento ? null : undefined),
      };

      const saved = contact
        ? await apiService.updateContact(contact.id, contactInput)
        : await apiService.createContact(contactInput);
      const savedId = contact?.id || (saved as { id?: string; data?: { id?: string } })?.id || (saved as { data?: { id?: string } })?.data?.id;

      // Campos personalizados alterados (vazio apaga)
      const changed = Object.fromEntries(Object.entries(fieldValues).filter(([k, v]) => (initialFieldValues[k] ?? '') !== v));
      if (savedId && Object.keys(changed).length) {
        const res = await fetchApi(`/api/custom-fields/contact/${savedId}`, { method: 'PUT', body: JSON.stringify({ values: changed }) });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          toast.error(`Contato salvo, mas os campos personalizados não: ${j.message || 'erro'}`);
          return;
        }
      }
      toast.success(contact ? 'Contato atualizado com sucesso' : 'Contato criado com sucesso');

      onSuccess();
    } catch (err: unknown) {
      const apiErr = err as { isQuotaError?: boolean; upgradeRequired?: boolean; message?: string };
      if (apiErr?.isQuotaError || apiErr?.upgradeRequired) {
        toast.error(apiErr.message || 'Limite de contatos atingido. Faça upgrade do seu plano.');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar contato');
    }
  };

  const categoryOptions = [
    { value: NO_CATEGORY, label: 'Sem categoria' },
    ...categories.map((cat) => ({ value: cat.id, label: cat.nome })),
  ];

  return (
    <FormPage
      backTo="/contatos"
      onBack={onCancel}
      backLabel="Contatos"
      title={contact ? 'Editar contato' : 'Novo contato'}
      description={contact ? 'Atualize as informações do contato.' : 'Preencha os dados para criar um novo contato.'}
      formId="contact-form"
      submitLabel={contact ? 'Salvar alterações' : 'Criar contato'}
      submitting={isSubmitting}
      onCancel={onCancel}
    >
        <form id="contact-form" onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-6">
          <FormSection title="Dados do contato">
          <Field label="Nome" description="Obrigatório" error={errors.nome?.message ? { message: errors.nome?.message, match: true } : undefined}>
            <Controller
              name="nome"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} aria-label="Nome" value={field.value} placeholder="Digite o nome completo" />
              )}
            />
          </Field>

          <Field label="Telefone" description="Obrigatório" error={errors.telefone?.message ? { message: errors.telefone?.message, match: true } : undefined}>
            <Controller
              name="telefone"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} aria-label="Telefone" value={field.value} placeholder="+55 11 99999-9999" />
              )}
            />
          </Field>

          <Field label="Email" description="Opcional" error={errors.email?.message ? { message: errors.email?.message, match: true } : undefined}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input onChange={(e) => field.onChange(e.target.value)} aria-label="Email" value={field.value ?? ''} placeholder="Digite o email (opcional)" type="email" />
              )}
            />
          </Field>

          <div>
            <label htmlFor="contact-birthday" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>Data de nascimento <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span></label>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>Para a mensagem automática de aniversário</p>
            <input id="contact-birthday" type="date" max={new Date().toISOString().slice(0, 10)} {...register('dataNascimento')}
              style={{ width: '100%', height: 36, border: '1px solid var(--border-strong)', borderRadius: 6, padding: '0 12px', fontSize: 13 }} />
          </div>

          <Controller
            name="categoriaId"
            control={control}
            render={({ field }) => (
              <Field label="Categoria">
                <Select value={(field.value ? categoryOptions.find((o) => o.value === field.value) ?? null : null)?.value ?? null} items={categoryOptions} onValueChange={(value: string | null) => field.onChange(value === NO_CATEGORY ? '' : value ?? '')} placeholder={loadingCategories ? 'Carregando...' : 'Selecione uma categoria'} disabled={loadingCategories} />
              </Field>
            )}
          />

          <Field label="Observações" description="Opcional">
            <Controller
              name="observacoes"
              control={control}
              render={({ field }) => (
                <InputArea onChange={(e) => field.onChange(e.target.value)} aria-label="Observações" value={field.value ?? ''} placeholder="Digite observações adicionais (opcional)" />
              )}
            />
          </Field>

          <ContactCustomFields
            fields={customFields}
            values={fieldValues}
            onChange={(key, value) => setFieldValues((v) => ({ ...v, [key]: value }))}
          />

          {contact?.perfexLeadId && (
            <div className="bg-[var(--tint-purple)] border border-[var(--purple-text)] rounded-md p-3">
              <p className="font-semibold !text-[var(--purple-text)] mb-1">ID do Lead no Perfex CRM</p>
              <code className="text-sm text-[var(--text)]">{contact.perfexLeadId}</code>
              <p className="!text-[var(--purple-text)] !text-xs mt-1">
                Este contato foi importado do Perfex CRM
              </p>
            </div>
          )}

          </FormSection>
        </form>
    </FormPage>
  );
}
